import React from "react";
import styles from "./Button.module.css";

export type ButtonVariant =
    | "primary"
    | "outline"
    | "cancel"
    | "danger"
    | "dangerOutline"
    | "ghost"
    | "soft"
    | "secondary";
export type ButtonShape = "default" | "pill";

export interface ButtonProps {
    label: string;
    onClick: (e?: React.MouseEvent) => void;
    className?: string;
    disabled?: boolean;
    showError?: () => void;
    isSmall?: boolean;
    preventDefault?: boolean;
    /** Preferred Studio API — use instead of legacy string classes when possible */
    variant?: ButtonVariant;
    shape?: ButtonShape;
    block?: boolean;
    /** Leading icon (e.g. Claude generate CTA sparkles) */
    icon?: React.ReactNode;
}

const variantClass: Record<ButtonVariant, string> = {
    primary: styles.primary,
    outline: styles.outline,
    cancel: styles.cancel,
    secondary: styles.cancel,
    danger: styles.danger,
    dangerOutline: styles.dangerOutline,
    ghost: styles.ghost,
    soft: styles.soft,
};

const LEGACY_VARIANT_TOKENS = new Set([
    "generate-button",
    "next-button",
    "cancel-button",
    "secondary-button",
    "delete-button",
    "delete-next-button",
    "remove-button",
    "remove-button-big",
    "btn-danger",
    "btn-outline",
    "btn-ghost",
    "btn-soft",
    "btn-primary",
    "btn-pill",
    "btn-shape-pill",
    "btn-shape-default",
    "btn-block",
    "btn-lg",
    "button",
]);

function has(tokens: Set<string>, ...names: string[]) {
    return names.some((n) => tokens.has(n));
}

function resolveLegacy(className: string): {
    variant: ButtonVariant;
    shape: ButtonShape;
    block: boolean;
    lg: boolean;
    passthrough: string[];
} {
    const list = className.split(/\s+/).filter(Boolean);
    const tokens = new Set(list);

    let variant: ButtonVariant = "primary";
    if (has(tokens, "cancel-button", "secondary-button")) variant = "cancel";
    else if (has(tokens, "delete-button", "delete-next-button", "remove-button", "remove-button-big", "btn-danger"))
        variant = "danger";
    else if (has(tokens, "btn-outline")) variant = "outline";
    else if (has(tokens, "btn-ghost")) variant = "ghost";
    else if (has(tokens, "btn-soft")) variant = "soft";
    else if (has(tokens, "generate-button", "next-button", "btn-primary", "button")) variant = "primary";

    let shape: ButtonShape = "default";
    // Cancel / outline / danger CTAs are pills in Studio (match Continuer / Enregistrer)
    if (
        has(tokens, "btn-pill", "btn-shape-pill") ||
        variant === "outline" ||
        variant === "cancel" ||
        variant === "danger"
    )
        shape = "pill";
    if (has(tokens, "btn-shape-default")) shape = "default";

    const block = tokens.has("btn-block");
    const lg = tokens.has("btn-lg");

    const passthrough = list.filter(
        (t) => t !== "button" && t !== "button-disabled" && t !== "button-small" && !LEGACY_VARIANT_TOKENS.has(t),
    );

    return { variant, shape, block, lg, passthrough };
}

const Button: React.FC<ButtonProps> = ({
    label,
    onClick,
    className = "",
    disabled = false,
    showError,
    isSmall = false,
    preventDefault = false,
    variant: variantProp,
    shape: shapeProp,
    block: blockProp = false,
    icon,
}) => {
    const legacy = resolveLegacy(className);
    const variant = variantProp ?? legacy.variant;
    // Prefer explicit shape; otherwise Studio CTAs (primary/cancel/…) default to pill
    const shape =
        shapeProp ??
        (variant === "primary" ||
        variant === "cancel" ||
        variant === "secondary" ||
        variant === "danger" ||
        variant === "dangerOutline" ||
        variant === "outline" ||
        variant === "soft"
            ? "pill"
            : legacy.shape);
    const block = blockProp || legacy.block;

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (preventDefault) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (!disabled) {
            onClick();
        } else if (showError) {
            showError();
        }
    };

    const passthrough =
        variantProp || shapeProp || blockProp
            ? legacy.passthrough
            : className
                  .split(/\s+/)
                  .filter(Boolean)
                  .filter((t) => t !== "button" && t !== "button-disabled" && t !== "button-small");

    const classNames = [
        styles.root,
        variantClass[variant],
        shape === "pill" ? styles.pill : "",
        block ? styles.block : "",
        legacy.lg ? styles.lg : "",
        isSmall ? styles.small : "",
        disabled ? styles.disabled : "",
        "button",
        ...passthrough,
        disabled ? "button-disabled" : "",
        isSmall ? "button-small" : "",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <button type="button" className={classNames} onClick={handleClick} disabled={disabled}>
            {icon}
            {label}
        </button>
    );
};

export default Button;
